"use client";

import { type ReactNode, useId } from "react";

import cn from "classnames";

export const SegmentedToggle = ({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  fontClassName = "font-sans",
  activeLabelClassName,
  inactiveLabelClassName,
  disabled = false,
}: SegmentedToggleProps) => {
  const id = useId();
  if (!options?.length) return null;

  const renderOptions = () => (
    <>
      {options.map((option) => {
        const isChecked = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isChecked}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            className={cn(
              "button focus-visible:ring-secondary relative z-0 flex min-h-9 min-w-0 flex-1 cursor-pointer items-center justify-center overflow-hidden rounded-full px-[3px] py-1.5 text-center text-ellipsis whitespace-nowrap uppercase transition-colors duration-200 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-60",
              {
                [`bg-secondary text-white ${activeLabelClassName ?? ""}`]:
                  isChecked,
                [`text-greyscale-headline bg-transparent ${inactiveLabelClassName ?? ""}`]:
                  !isChecked,
              },
            )}>
            {option.label}
          </button>
        );
      })}
    </>
  );

  return (
    <section
      id={id}
      role="radiogroup"
      aria-label={ariaLabel}
      aria-disabled={disabled}
      className={cn(
        "bg-greyscale-background border-primary-400 relative mx-auto flex w-full max-w-full gap-2 overflow-hidden rounded-full border-2 p-1 text-center text-base leading-6 font-bold select-none",
        fontClassName,
        className,
      )}>
      {renderOptions()}
    </section>
  );
};

type SegmentedToggleOption = {
  value: string;
  label: ReactNode;
};

type SegmentedToggleProps = {
  options: SegmentedToggleOption[];
  value: string;
  onChange: (value: string) => void;
  ariaLabel: string;
  className?: string;
  fontClassName?: string;
  activeLabelClassName?: string;
  inactiveLabelClassName?: string;
  disabled?: boolean;
};
